import { Award, Disc3, MicOff, Trophy } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { SpaceAvatar } from "@/components/SpaceAvatar";
import { useRandomWheel } from "@/components/RandomWheel";

export const daysSince = (iso) => (iso ? Math.floor((Date.now() - new Date(iso)) / 86400000) : null);

export const FunCorner = ({ fun, reload }) => {
  const { team } = useSettings();
  const [spinWheel, wheel] = useRandomWheel(reload);
  const w = team?.wallboard || {};
  if (!fun) return null;
  const resetMute = () => api.post("/fun/mute-reset").then(() => { toast("Counter reset. Back to day zero."); reload(); }).catch((e) => toast.error(errMsg(e)));
  const mute = daysSince(fun.mute.reset_at);
  return (
    <div className="grid gap-4 sm:grid-cols-2" data-testid="fun-corner">
      {wheel}
      {w.tea_round && (
        <div className="rounded-2xl border bg-card/70 p-5" data-testid="tea-round-card">
          <p className="flex items-center gap-2 font-display font-semibold"><Disc3 className="h-4 w-4 text-amber-300" /> Random Wheel</p>
          <p className="mt-3 font-display text-2xl font-bold" data-testid="tea-round-name">{fun.tea_round?.name || "Not spun yet"}</p>
          {fun.tea_round && <p className="text-xs text-muted-foreground">Last spun by {fun.tea_round.by}</p>}
          <button onClick={spinWheel} className="mt-3 rounded-full bg-amber-400 px-4 py-1.5 text-sm font-semibold text-black transition-colors hover:bg-amber-300" data-testid="tea-round-spin">Spin the wheel</button>
        </div>
      )}
      {w.mute_counter && (
        <div className="rounded-2xl border bg-card/70 p-5" data-testid="mute-counter-card">
          <p className="flex items-center gap-2 font-display font-semibold"><MicOff className="h-4 w-4 text-pink-300" /> Days Since We Fucked Up</p>
          <p className="mt-3 font-mono text-4xl font-bold" data-testid="mute-counter-days">{mute ?? "–"}</p>
          <p className="text-xs text-muted-foreground">Record: {Math.max(fun.mute.best_days || 0, mute || 0)} days{fun.mute.by ? ` · last reset by ${fun.mute.by}` : ""}</p>
          <button onClick={resetMute} className="mt-3 rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors hover:bg-white/5" data-testid="mute-counter-reset">We fucked up</button>
        </div>
      )}
      {w.leaderboard && (
        <div className="rounded-2xl border bg-card/70 p-5" data-testid="leaderboard-card">
          <p className="flex items-center gap-2 font-display font-semibold"><Trophy className="h-4 w-4 text-amber-300" /> This week</p>
          <ol className="mt-3 space-y-1.5">
            {fun.leaderboard.map((b, i) => (
              <li key={b.username} className="flex items-center gap-2 text-sm" data-testid={`leaderboard-${b.username}`}>
                <span className="w-4 font-mono text-muted-foreground">{i + 1}</span><SpaceAvatar space={b.username} /><span className="flex-1">{b.name}</span>
                <span className="font-mono text-xs text-muted-foreground">{b.closed} closed · {b.updates} updates</span>
              </li>
            ))}
          </ol>
        </div>
      )}
      {fun.awards?.length > 0 && (
        <div className="rounded-2xl border bg-card/70 p-5" data-testid="awards-card">
          <p className="flex items-center gap-2 font-display font-semibold"><Award className="h-4 w-4 text-cyan-300" /> Weekly awards <span className="text-xs font-normal text-muted-foreground">{new Date().getDay() === 5 ? "final" : "so far"}</span></p>
          <ul className="mt-3 space-y-2">
            {fun.awards.map((a) => <li key={a.title} className="text-sm"><b>{a.title}</b>: {a.name} <span className="text-xs text-muted-foreground">({a.value} {a.blurb})</span></li>)}
          </ul>
        </div>
      )}
    </div>
  );
};
